import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { PricingProvider } from '@/contexts/PricingContext';
import { Toaster } from '@/components/ui/sonner';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import { CookieConsent } from '@/components/CookieConsent';
import { PageLoader } from '@/components/PageLoader';
import { RouteTransition } from '@/components/motion/RouteTransition';

import * as Pages from '@/lazyPages';

const {
  Landing,
  Login,
  Register,
  Onboarding,
  Dashboard,
  Practice,
  Pricing,
  EducatorPricingPage,
  TutorCabinet,
  TutorAnalytics,
  EducatorInvitations,
  TutorLayout,
  TutorStudentsPage,
  TutorSchedulePage,
  TutorMaterialsPage,
  TutorFlashcardsPage,
  TutorCrmPage,
  TutorBoardPage,
  TutorHomeworkPage,
  TutorAiPage,
  ForTutors,
  ForParents,
  EgeLandingPage,
  ParentOnboarding,
  ParentCabinet,
  ParentLayout,
  ParentPricingPage,
  ParentChildrenPage,
  ParentProgressPage,
  ParentNotificationsPage,
  Terms,
  Privacy,
  Contact,
  Settings,
  AdminPanel,
  MyAppeals,
  Referral,
  Cookies,
  Refund,
  PaymentSuccess,
  PaymentFail,
  Achievements,
  Analytics,
  Theory,
  AiDebugPage,
  EmailVerification,
  ForgotPassword,
  ResetPassword,
  OAuthCallback,
  VariantResults,
  PracticeSessionResults,
  NotFound,
} = Pages;

const FeedbackChatWidget = lazy(() =>
  import(/* webpackChunkName: "feedback-chat" */ '@/components/FeedbackChatWidget')
);
import { LANDING_FAQ_MAIN_ENTITY } from '@/lib/landingSeoFaq';
import { SITE_URL, applyClientSeo } from '@/lib/seoApply';
import { EDUCATOR_SUITE_ENABLED } from '@/lib/educators';
import { EducatorSuiteComingSoonFullPage } from '@/components/educator/EducatorSuiteComingSoon';

const EducatorCabinetPage = EDUCATOR_SUITE_ENABLED ? TutorCabinet : EducatorSuiteComingSoonFullPage;
const EducatorAnalyticsPage = EDUCATOR_SUITE_ENABLED ? TutorAnalytics : EducatorSuiteComingSoonFullPage;
const EducatorInvitationsPage = EDUCATOR_SUITE_ENABLED ? EducatorInvitations : EducatorSuiteComingSoonFullPage;
const ParentOnboardingPage = EDUCATOR_SUITE_ENABLED ? ParentOnboarding : EducatorSuiteComingSoonFullPage;
const ParentCabinetPage = EDUCATOR_SUITE_ENABLED ? ParentCabinet : EducatorSuiteComingSoonFullPage;
const ParentChildrenPageRoute = EDUCATOR_SUITE_ENABLED ? ParentChildrenPage : EducatorSuiteComingSoonFullPage;
const ParentProgressPageRoute = EDUCATOR_SUITE_ENABLED ? ParentProgressPage : EducatorSuiteComingSoonFullPage;
const ParentNotificationsPageRoute = EDUCATOR_SUITE_ENABLED ? ParentNotificationsPage : EducatorSuiteComingSoonFullPage;
const ParentPricingPageRoute = EDUCATOR_SUITE_ENABLED ? ParentPricingPage : EducatorSuiteComingSoonFullPage;

// SEO-оптимизация: централизованные метаданные по публичным и приватным роутам.
const SEO_BY_ROUTE = [
  {
    match: (pathname) => pathname === '/',
    title: 'Подготовка к ЕГЭ с ИИ — РешAI',
    description:
      'Решай задания ЕГЭ онлайн, получай проверку от ИИ как на реальном экзамене, анализируй ошибки и улучшай результат.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/`,
    type: 'website',
    // SEO: Product/Service — тарифы синхронизированы с backend/utils/constants.py (token_packs).
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'РешAI',
        url: SITE_URL,
        inLanguage: 'ru-RU',
        description:
          'Платформа подготовки к ЕГЭ с ИИ: практика, проверка письменных работ и пробников, аналитика.',
        publisher: {
          '@type': 'Organization',
          name: 'РешAI',
          url: SITE_URL,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'РешAI',
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Web',
        url: SITE_URL,
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'RUB',
          lowPrice: '699',
          highPrice: '1999',
          offerCount: 3,
          availability: 'https://schema.org/InStock',
          url: `${SITE_URL}/pricing`,
        },
        description:
          'Платформа подготовки к ЕГЭ: практика по заданиям ФИПИ, проверка письменных работ и пробников с помощью ИИ, аналитика прогресса.',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: LANDING_FAQ_MAIN_ENTITY,
      },
    ],
  },
  {
    match: (pathname) => pathname === '/pricing',
    title: 'Тарифы РешAI — подписка для подготовки к ЕГЭ',
    description:
      'Тарифы РешAI: подготовка к ЕГЭ, проверка письменных работ ИИ, пробники, аналитика и доступ через наставника.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/pricing`,
    type: 'website',
  },
  {
    match: (pathname) => pathname === '/for-tutors',
    title: 'РешAI для репетиторов — проверка ЕГЭ с ИИ',
    description:
      'РешAI для репетиторов: назначайте задания, проверяйте работы учеников с ИИ, отслеживайте прогресс и экономьте время.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/for-tutors`,
    type: 'website',
  },
  {
    match: (pathname) => pathname === '/for-parents',
    title: 'РешAI для родителей — контроль подготовки ребёнка к ЕГЭ',
    description:
      'РешAI для родителей: следите за прогрессом ребёнка удалённо — домашка, аналитика, streak и результаты пробников в одном кабинете.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/for-parents`,
    type: 'website',
  },
  {
    match: (pathname) => pathname === '/for-teachers',
    title: 'РешAI для репетиторов',
    description: 'РешAI для репетиторов: задания, проверка с ИИ, аналитика учеников.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/for-tutors`,
    type: 'website',
  },
  {
    match: (pathname) => pathname === '/ege' || pathname.startsWith('/ege/'),
    title: 'Подготовка к ЕГЭ с ИИ — РешAI',
    description:
      'Подготовка к ЕГЭ по предметам: задания, пробники, проверка письменных работ ИИ и аналитика прогресса.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/ege`,
    type: 'website',
  },
  {
    match: (pathname) => pathname === '/contact',
    title: 'Контакты РешAI — поддержка сервиса ЕГЭ',
    description:
      'Свяжитесь с командой РешAI: вопросы по платформе подготовки к ЕГЭ, тарифам, сотрудничеству и технической поддержке.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/contact`,
    type: 'website',
  },
  {
    match: (pathname) => pathname === '/referral',
    title: 'Реферальная программа РешAI — бонусы за приглашения',
    description:
      'Приглашайте друзей в РешAI и получайте бонусы. Реферальная программа для подготовки к ЕГЭ с ИИ.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/referral`,
    type: 'website',
  },
  {
    match: (pathname) => pathname === '/terms',
    title: 'Условия использования — РешAI',
    description: 'Официальные условия использования сервиса РешAI для подготовки к ЕГЭ.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/terms`,
    type: 'article',
  },
  {
    match: (pathname) => pathname === '/privacy',
    title: 'Политика конфиденциальности — РешAI',
    description:
      'Политика конфиденциальности РешAI: какие данные собираются, как используются и как защищаются.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/privacy`,
    type: 'article',
  },
  {
    match: (pathname) => pathname === '/cookies',
    title: 'Политика cookies — РешAI',
    description:
      'Как РешAI использует cookies для улучшения подготовки к ЕГЭ и работы платформы.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/cookies`,
    type: 'article',
  },
  {
    match: (pathname) => pathname === '/refund',
    title: 'Возврат средств — РешAI',
    description: 'Условия возврата средств и порядок обращения в сервисе РешAI.',
    robots: 'index,follow',
    canonical: `${SITE_URL}/refund`,
    type: 'article',
  },
  {
    match: (pathname) => pathname === '/payment/success' || pathname === '/payment/fail',
    title: 'Статус оплаты — РешAI',
    description: 'Проверка статуса оплаты подписки.',
    robots: 'noindex,nofollow',
    canonical: `${SITE_URL}/`,
    type: 'website',
  },
  {
    match: (pathname) =>
      pathname === '/login' ||
      pathname === '/register' ||
      pathname === '/verify-email' ||
      pathname === '/forgot-password' ||
      pathname === '/reset-password' ||
      pathname.startsWith('/oauth/'),
    title: 'Вход и регистрация — РешAI',
    description: 'Вход и регистрация в платформе РешAI.',
    robots: 'noindex,nofollow',
    canonical: `${SITE_URL}/`,
    type: 'website',
  },
  {
    match: (pathname) =>
      pathname.startsWith('/onboarding') ||
      pathname.startsWith('/dashboard') ||
      pathname.startsWith('/practice') ||
      pathname.startsWith('/analytics') ||
      pathname.startsWith('/settings') ||
      pathname.startsWith('/tutor') ||
      pathname.startsWith('/teacher') ||
      pathname.startsWith('/parent') ||
      pathname.startsWith('/admin') ||
      pathname.startsWith('/my-appeals') ||
      pathname.startsWith('/achievements') ||
      pathname.startsWith('/theory') ||
      pathname.startsWith('/variant-results') ||
      pathname.startsWith('/practice-results'),
    title: 'Личный кабинет — РешAI',
    description: 'Персональный раздел пользователя платформы РешAI.',
    robots: 'noindex,nofollow',
    canonical: `${SITE_URL}/`,
    type: 'website',
  },
];

function SeoManager() {
  const location = useLocation();

  React.useEffect(() => {
    // SEO-оптимизация: обновляем мета-теги и JSON-LD при смене клиентского роута.
    const pathname = location.pathname;
    const seo =
      SEO_BY_ROUTE.find((item) => item.match(pathname)) || {
        title: 'РешAI — подготовка к ЕГЭ с ИИ',
        description: 'Онлайн-подготовка к ЕГЭ с ИИ: практика, проверка и аналитика.',
        robots: 'index,follow',
        canonical: `${SITE_URL}${pathname}`,
        type: 'website',
      };
    applyClientSeo(seo, pathname);
  }, [location.pathname]);

  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader className="min-h-screen w-full" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader className="min-h-screen w-full" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <div className="min-h-[100dvh] min-h-screen flex flex-col pb-safe px-safe">
      <SeoManager />
      <ScrollToTop />

      <Navbar />
      <div className="flex-1 flex flex-col">
      <Suspense
        fallback={
          <PageLoader className="min-h-[calc(100dvh-7rem)] w-full flex-1 sm:min-h-[calc(100dvh-8rem)]" />
        }
      >
      <RouteTransition className="flex-1 flex flex-col">
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/oauth/:provider/callback" element={<OAuthCallback />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pricing/tutors" element={<EducatorPricingPage standalone />} />
        <Route path="/pricing/parents" element={<ParentPricingPage standalone />} />
        <Route path="/how-it-works" element={<Navigate to="/#how-it-works" replace />} />
        <Route path="/for-tutors" element={<ForTutors />} />
        <Route path="/for-parents" element={<ForParents />} />
        <Route path="/for-teachers" element={<Navigate to="/for-tutors" replace />} />
        <Route path="/ege" element={<EgeLandingPage />} />
        <Route path="/ege/:subjectSlug" element={<EgeLandingPage />} />
        {/* ОГЭ убран — клиентский фолбэк-редирект на ЕГЭ-хаб; настоящие 301 — в public/_redirects */}
        <Route path="/oge" element={<Navigate to="/ege" replace />} />
        <Route path="/oge/:subjectSlug" element={<Navigate to="/ege" replace />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/referral" element={<Referral />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/fail" element={<PaymentFail />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/practice"
          element={
            <ProtectedRoute>
              <Practice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/practice/:subjectSlug"
          element={
            <ProtectedRoute>
              <Practice />
            </ProtectedRoute>
          }
        />
        <Route path="/tutor">
          <Route
            element={
              <ProtectedRoute>
                <TutorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<EducatorCabinetPage />} />
            <Route path="pricing" element={<EducatorPricingPage />} />
            <Route path="crm" element={<TutorCrmPage />} />
            <Route path="students" element={<TutorStudentsPage />} />
            <Route path="schedule" element={<TutorSchedulePage />} />
            <Route path="homework" element={<TutorHomeworkPage />} />
            <Route path="materials" element={<TutorMaterialsPage />} />
            <Route path="board" element={<TutorBoardPage />} />
            <Route path="flashcards" element={<TutorFlashcardsPage />} />
            <Route path="analytics" element={<EducatorAnalyticsPage />} />
            <Route path="invitations" element={<EducatorInvitationsPage />} />
            <Route path="ai" element={<TutorAiPage />} />
          </Route>
        </Route>
        <Route path="/teacher" element={<Navigate to="/tutor" replace />} />
        <Route path="/teacher/*" element={<Navigate to="/tutor" replace />} />
        <Route path="/parent">
          <Route
            element={
              <ProtectedRoute>
                <ParentLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ParentCabinetPage />} />
            <Route path="children" element={<ParentChildrenPageRoute />} />
            <Route path="progress" element={<ParentProgressPageRoute />} />
            <Route path="notifications" element={<ParentNotificationsPageRoute />} />
            <Route path="pricing" element={<ParentPricingPageRoute />} />
          </Route>
          <Route path="students" element={<Navigate to="/parent/children" replace />} />
          <Route path="analytics" element={<Navigate to="/parent/progress" replace />} />
          <Route path="invitations" element={<Navigate to="/parent/children" replace />} />
        </Route>
        <Route
          path="/parent/onboarding"
          element={
            <ProtectedRoute>
              <ParentOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />
        <Route
          path="/my-appeals"
          element={
            <ProtectedRoute>
              <MyAppeals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/achievements"
          element={
            <ProtectedRoute>
              <Achievements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/:subjectSlug"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/theory"
          element={
            <ProtectedRoute>
              <Theory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/theory/:subjectSlug"
          element={
            <ProtectedRoute>
              <Theory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dev/ai-debug"
          element={
            <AdminRoute>
              <AiDebugPage />
            </AdminRoute>
          }
        />
        <Route
          path="/variant-results/:variantId"
          element={
            <ProtectedRoute>
              <VariantResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/practice-results/:sessionId"
          element={
            <ProtectedRoute>
              <PracticeSessionResults />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </RouteTransition>
      </Suspense>
      </div>
      <Footer />
      <Suspense fallback={null}>
        <FeedbackChatWidget />
      </Suspense>
      <CookieConsent />
    </div>
  );
}

function ResponsiveToaster() {
  const [position, setPosition] = React.useState(() => {
    if (typeof window === 'undefined') return 'bottom-right';
    return window.matchMedia('(max-width: 639px)').matches ? 'bottom-center' : 'bottom-right';
  });

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const sync = () => setPosition(mq.matches ? 'bottom-center' : 'bottom-right');
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return <Toaster position={position} />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <PricingProvider>
            <AppRoutes />
            <ResponsiveToaster />
          </PricingProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
